import { Add, Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip as MuiChip,
  Divider,
  Grid,
  MenuItem,
  TablePagination,
  TextField,
  Typography
} from '@mui/material'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { ClusterShipperDto } from 'dtos/clusterShipperDto'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ClusterShippersListCard from './ClusterShippersListCard'
import { GreenText } from 'components'
import ClusterShippersAddEditDialog from './id/ClusterShippersAddEditDialog'
import { ClusterShippersParameters } from 'parameters'
import { useDebounce } from 'utils'
import { enqueueSnackbar } from 'notistack'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'

const sessionStorageKey = 'clusterShippers:parameters'

export default function ClusterShippers() {
  const layout = useLayout()
  const navigate = useNavigate()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const defaultParameters: ClusterShippersParameters = {
    page: 0,
    pageSize: 10,
    orderBy: 'clusterShipperNumber',
    order: 'asc'
  }

  const [isCreatingClusterShipper, setIsCreatingClusterShipper] = useState<boolean>(false)
  const [isGettingClusterShippers, setIsGettingClusterShippers] = useState<boolean>(false)
  const [clusterShippers, setClusterShippers] = useState<ClusterShipperDto[]>([])
  const [clusterShipper, setClusterShipper] = useState<ClusterShipperDto>(new ClusterShipperDto())
  const [clusterShipperAddEditDialogOpen, setClusterShipperAddEditDialogOpen] = useState<boolean>(false)

  const [parameters, setParameters] = useState<ClusterShippersParameters>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultParameters
  )

  const [totalCount, setTotalCount] = useState<number>(0)
  const [includeCompletedShippers, setIncludeCompletedShippers] = useState<boolean>(false)

  const getClusterShippers = useDebounce((parameters: ClusterShippersParameters) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(parameters))

    setIsGettingClusterShippers(true)
    controllers
      .getClusterShippers(parameters)
      .then(response => {
        setClusterShippers(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingClusterShippers(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    getClusterShippers(parameters)
  }, [getClusterShippers, parameters])

  useEffect(() => {
    layout.setTitle('Cluster Shippers')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingClusterShippers)
  }, [layout, isGettingClusterShippers])

  return (
    <>
      <ClusterShippersAddEditDialog
        initialValues={clusterShipper}
        isLoading={isCreatingClusterShipper}
        onClose={() => {
          setClusterShipperAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsCreatingClusterShipper(true)
          controllers
            .createClusterShipper(values)
            .then(_ => {
              enqueueSnackbar('Cluster Shipper Created Successfully!', { variant: 'success' })
              getClusterShippers(parameters)
              setClusterShipperAddEditDialogOpen(false)
            })
            .finally(() => {
              setIsCreatingClusterShipper(false)
            })
        }}
        open={clusterShipperAddEditDialogOpen}
      />

      <Grid container spacing={1} alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Cluster Shippers ({totalCount!})
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm='auto' alignContent='center'>
                  <Grid item container xs={12}>
                    <ListFilterGroup>
                      <TextField
                        fullWidth
                        label='Filter Field'
                        margin='dense'
                        onChange={e => {
                          setFilterField(e.target.value)
                          setFilterValue('')
                        }}
                        select
                        size='small'
                        sx={{ minWidth: 200 }}
                        value={filterField || ''}
                      >
                        <MenuItem value='clusterShipperNumber'>Cluster Shipper #</MenuItem>

                        <MenuItem value='clusterConversionCompany'>Cluster Conversion Company</MenuItem>

                        <MenuItem value='exporterCompany'>Exporter</MenuItem>

                        <MenuItem value='buyerCompany'>Buyer Company</MenuItem>

                        <MenuItem value='dropOffDate'>Drop Off Date</MenuItem>

                        {/* <MenuItem value='vin'>VIN</MenuItem>

                        <MenuItem value='modelYear'>Model Year</MenuItem>

                        <MenuItem value='make'>Make</MenuItem>

                        <MenuItem value='model'>Model</MenuItem> */}
                      </TextField>

                      {[
                        '',
                        'clusterShipperNumber',
                        'clusterConversionCompany',
                        'exporterCompany',
                        'buyerCompany'
                        // 'vin',
                        // 'modelYear',
                        // 'make',
                        // 'model'
                      ].includes(filterField) && (
                        <Grid container>
                          <Grid item xs={12} sm='auto'>
                            <TextField
                              disabled={!filterField}
                              label='Filter Value'
                              margin='dense'
                              onChange={e => {
                                setFilterValue(e.target.value)
                              }}
                              size='small'
                              value={filterValue || ''}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {filterField === 'dropOffDate' && (
                        <>
                          <Grid container>
                            <Grid item xs={12} sm='auto'>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                                label='Filter Date From'
                                onChange={e => {
                                  setFilterValueFrom(e.target.value)
                                }}
                                type='date'
                                value={filterValueFrom}
                              />
                            </Grid>

                            <Grid item xs={12} sm='auto'>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                                label='Filter Date To'
                                onChange={e => {
                                  setFilterValueTo(e.target.value)
                                }}
                                type='date'
                                value={filterValueTo}
                              />
                            </Grid>
                          </Grid>
                        </>
                      )}

                      <Button
                        color='inherit'
                        disabled={
                          !filterField ||
                          !filterValue ||
                          (filterField === 'dropOffDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                        }
                        size='medium'
                        variant='contained'
                        onClick={() => {
                          if (filterField === 'dropOffDate') {
                            setParameters({
                              ...parameters,
                              dropOffDateFrom: filterValueFrom,
                              dropOffDateTo: filterValueTo
                            })
                          } else {
                            setParameters({
                              ...parameters,
                              [filterField]: filterValue
                            })
                          }

                          setFilterField('')
                          setFilterValue('')
                          setFilterValueFrom('')
                          setFilterValueTo('')
                        }}
                      >
                        APPLY FILTER
                      </Button>

                      <Button
                        color='secondary'
                        disabled={
                          !Boolean(
                            parameters.dropOffDateFrom ||
                              parameters.dropOffDateTo ||
                              parameters.clusterShipperNumber ||
                              parameters.clusterConversionCompany ||
                              parameters.exporterCompany ||
                              parameters.buyerCompany
                          )
                        }
                        size='medium'
                        variant='text'
                        onClick={() => {
                          setParameters({
                            ...parameters,
                            dropOffDateFrom: undefined,
                            dropOffDateTo: undefined,
                            clusterShipperNumber: undefined,
                            clusterConversionCompany: undefined,
                            exporterCompany: undefined,
                            buyerCompany: undefined
                          })
                        }}
                      >
                        RESET FILTERS
                      </Button>

                      <Checkbox
                        color='primary'
                        checked={includeCompletedShippers}
                        onChange={() => setIncludeCompletedShippers(!includeCompletedShippers)}
                      />

                      <Typography variant='body2'>Include Completed Shippers</Typography>
                    </ListFilterGroup>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto' container alignItems='center'>
                  <Grid item>
                    <Button
                      color='primary'
                      endIcon={<Add />}
                      onClick={() => {
                        setClusterShipper(new ClusterShipperDto())
                        setClusterShipperAddEditDialogOpen(true)
                      }}
                      variant='text'
                    >
                      ADD SHIPPER
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {parameters.clusterShipperNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Cluster Shipper # contains "${parameters.clusterShipperNumber}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            clusterShipperNumber: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.clusterConversionCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Cluster Conversion Company contains "${parameters.clusterConversionCompany}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            clusterConversionCompany: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.exporterCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Exporter Company contains "${parameters.exporterCompany}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            exporterCompany: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.buyerCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Buyer Company contains "${parameters.buyerCompany}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            buyerCompany: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.dropOffDateFrom && parameters.dropOffDateTo && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Drop Off Date from "${moment(parameters.dropOffDateFrom).format('M/D/yyyy')}" to "${moment(
                          parameters.dropOffDateTo
                        ).format('M/D/yyyy')}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            dropOffDateFrom: undefined,
                            dropOffDateTo: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {clusterShippers.map(clusterShipper => (
          <Grid item xs={12} key={clusterShipper.clusterShipperGuid}>
            <ClusterShippersListCard clusterShipper={clusterShipper}>
              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    navigate(`/cluster-shippers/${clusterShipper.clusterShipperGuid}`)
                  }}
                >
                  MANAGE SHIPPER
                </Button>
              </Grid>
            </ClusterShippersListCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <GreenText>
            {`**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:
   - Cluster Shipper #,
   - Cluster Conversion Company,
   - Exporter,
   - Buyer Company,
   - Drop Off Date

***Filter Value:***

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":
   - Cluster Shipper #: Displays a free-type filter field.
   - Cluster Conversion Company: Displays a free-type filter field.
   - Exporter: Displays a free-type filter field.
   - Buyer Company: Displays a free-type filter field.
   - Drop Off Date: Displays Filter Date From and Filter Date To.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Include Completed Shippers Checkbox:**

Clicking this checkbox will include Completed Shippers on the Cluster Shippers List Screen

**ADD SHIPPER Button:**

Clicking this button will open the “Add Cluster Shipper” dialog.

**Cluster Shipper Number:**

Cluster Shipper # is a read-only label that’s value is auto populated on save when Adding a Cluster Shipper

**Cluster Conversion Company:**

A read-only label that displays the value selected in the “Add Cluster Shipper” dialog

**Exporter:**

A read-only label that displays the value selected in the “Add Cluster Shipper” dialog

**Buyer:**

A read-only label that displays the value selected in the “Add Cluster Shipper” dialog

**Drop off Date:**

A read-only field that displays the date the “3rd Party Conversion Delivery” photo was uploaded to the Cluster Shipper.

**Cluster Conversions Completed:**

A read-only field that displays the completion status of cluster conversions associated with the shipper:

If all clusters associated with the shipper have been scanned back in (“Cluster Conversion Completed”), this field shows the date the last cluster was scanned back in at BWI.

If any clusters associated with the shipper have not been scanned back in, this field displays a “-”.`}
          </GreenText>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => {
              setParameters({
                ...parameters,
                page
              })
            }}
            onRowsPerPageChange={e => {
              setParameters({
                ...parameters,
                pageSize: e.target.value as unknown as number
              })
            }}
            page={parameters.page}
            rowsPerPage={parameters.pageSize}
            labelRowsPerPage={
              <Grid container spacing={1}>
                <Grid item>Order By:</Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, orderBy: e.target.value })
                    }}
                    select
                    size='small'
                    value={parameters.orderBy}
                    variant='standard'
                  >
                    <MenuItem value='clusterShipperNumber'>Cluster Shipper #</MenuItem>

                    <MenuItem value='clusterConversionCompany'> Cluster Conversion Company</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='buyer'>Buyer</MenuItem>

                    <MenuItem value='dropOffDate'>Drop Off Date</MenuItem>
                  </TextField>
                </Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                    }}
                    select
                    size='small'
                    value={parameters.order}
                    variant='standard'
                  >
                    <MenuItem value='asc'>Ascending</MenuItem>

                    <MenuItem value='desc'>Descending</MenuItem>
                  </TextField>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>Cards Per Page:</Grid>
              </Grid>
            }
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
